/**
 * Mobile footer navbar configuration store.
 *
 * Module-singleton state so the footer bar (BaseLayout) and the settings page
 * stay in sync while both are mounted. Persists through the standard portal
 * settings pipeline (localStorage + cookie + POST /auth/settings → identity
 * `extra` jsonb), keyed `mobileNavbar`.
 */
import { usePage } from "@inertiajs/vue3";
import { computed, ref, type ComputedRef, type Ref } from "vue";

import {
    getSessionSettings,
    setSessionSettings,
    type MobileNavbarSettings,
} from "@ja/portal-query";

import type { LayoutConfig, LayoutItem } from "../types/layout";

export const MOBILE_NAVBAR_MIN_BUTTONS = 3;
export const MOBILE_NAVBAR_MAX_BUTTONS = 6;

export const MOBILE_NAVBAR_DEFAULTS: MobileNavbarSettings = {
    enabled: false,
    maxButtons: 5,
    items: [],
};

/** A sidebar item eligible for the mobile navbar (route-based, no event items). */
export type NavbarCandidate = Pick<
    LayoutItem,
    "key" | "label" | "route" | "icon" | "translationPrefix" | "inertia"
>;

const state: Ref<MobileNavbarSettings | null> = ref(null);

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

/**
 * Default pinned items when the user has never configured the navbar:
 * every route item of the untabbed sidebar sections — the always-visible
 * employee pages (Formations, Mes demandes, Mon Horaire, Mon Profil), not
 * the tabbed admin/management sections.
 */
function defaultItemKeys(config: LayoutConfig): string[] {
    const keys: string[] = [];
    for (const section of config.sidebar ?? []) {
        if (section.dataSource || section.tab?.key || section.pinBottom) continue;
        for (const item of section.items ?? []) {
            if (!item.route || item.event || keys.includes(item.key)) continue;
            keys.push(item.key);
        }
    }
    return keys;
}

/** Coerce any persisted value into a safe, well-formed settings object. */
function normalize(raw: unknown): MobileNavbarSettings {
    const source = (raw ?? {}) as Partial<MobileNavbarSettings>;
    const items = Array.isArray(source.items)
        ? [...new Set(source.items.filter((k): k is string => typeof k === "string"))]
        : [...MOBILE_NAVBAR_DEFAULTS.items];
    return {
        enabled: source.enabled === true,
        maxButtons: clamp(
            Number(source.maxButtons) || MOBILE_NAVBAR_DEFAULTS.maxButtons,
            MOBILE_NAVBAR_MIN_BUTTONS,
            MOBILE_NAVBAR_MAX_BUTTONS,
        ),
        items,
    };
}

export function useMobileNavbar(): {
    settings: ComputedRef<MobileNavbarSettings>;
    candidates: ComputedRef<NavbarCandidate[]>;
    dashboard: ComputedRef<{ route: string; icon: string; label?: string; labelKey?: string }>;
    displayedItems: ComputedRef<NavbarCandidate[]>;
    availableItems: ComputedRef<NavbarCandidate[]>;
    update: (partial: Partial<MobileNavbarSettings>) => void;
    reset: () => void;
} {
    const page = usePage();

    const layoutConfig = computed((): LayoutConfig => {
        return ((page.props as any).layout as LayoutConfig | undefined) ?? { sidebar: [], userMenu: [] };
    });

    if (state.value === null) {
        const stored = (page.props as any).settings?.mobileNavbar ?? getSessionSettings().mobileNavbar;
        state.value = stored
            ? normalize(stored)
            : normalize({ ...MOBILE_NAVBAR_DEFAULTS, items: defaultItemKeys(layoutConfig.value) });
    }

    const settings = computed(() => state.value ?? MOBILE_NAVBAR_DEFAULTS);

    /**
     * Flat, deduplicated list of route-based sidebar items the user may pin.
     * Event-bound items (e.g. emergency dialogs) and data-source sections
     * (recent history) are excluded — the navbar is pure navigation.
     */
    const candidates = computed((): NavbarCandidate[] => {
        const seen = new Map<string, NavbarCandidate>();
        for (const section of layoutConfig.value.sidebar ?? []) {
            if (section.dataSource) continue;
            for (const item of section.items ?? []) {
                if (!item.route || item.event || seen.has(item.key)) continue;
                seen.set(item.key, {
                    key: item.key,
                    label: item.label,
                    route: item.route,
                    icon: item.icon,
                    translationPrefix: item.translationPrefix,
                    inertia: item.inertia,
                });
            }
        }
        return [...seen.values()];
    });

    const dashboard = computed(() => {
        const config = layoutConfig.value.dashboard;
        return {
            route: config?.route ?? "/",
            icon: config?.icon ?? "Home",
            label: config?.label,
            labelKey: config?.labelKey,
        };
    });

    /** Selected items in user order, dropping keys that no longer resolve (permission/app change). */
    const displayedItems = computed((): NavbarCandidate[] => {
        const byKey = new Map(candidates.value.map((c) => [c.key, c]));
        return settings.value.items
            .map((key) => byKey.get(key))
            .filter((c): c is NavbarCandidate => c !== undefined);
    });

    const availableItems = computed((): NavbarCandidate[] => {
        const selected = new Set(settings.value.items);
        return candidates.value.filter((c) => !selected.has(c.key));
    });

    function update(partial: Partial<MobileNavbarSettings>): void {
        const next = normalize({ ...settings.value, ...partial });
        state.value = next;
        void setSessionSettings({ mobileNavbar: next });
    }

    function reset(): void {
        update({
            ...MOBILE_NAVBAR_DEFAULTS,
            enabled: settings.value.enabled,
            items: defaultItemKeys(layoutConfig.value),
        });
    }

    return { settings, candidates, dashboard, displayedItems, availableItems, update, reset };
}
